import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { BalanceCard } from '@/components/BalanceCard';
import { ErrorDisplay } from '@/components/ErrorDisplay';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageContainer } from '@/components/PageContainer';

import { useAuth } from '@/hooks/useAuth';
import { useDashboardData } from '@/hooks/useDashboardData';

import { getAvatarColor, getInitials } from '@/utils/stringUtils';

function Profile() {
  const { user } = useAuth();
  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
    refetch,
  } = useDashboardData();

  if (!user) return null;

  return (
    <PageContainer>
      <header className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-gray-900">My Account</h1>
        <p className="text-sm text-gray-600">View your Unleash account details</p>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch">
        {/* Profile Information */}
        <div className="animate-fade-in-up h-full opacity-0" style={{ animationDelay: '100ms' }}>
          <div
            data-testid="profile-card"
            className="flex h-full min-h-[200px] flex-col rounded-lg bg-white p-6 shadow-md"
          >
            <h2 className="mb-6 text-xl font-bold text-gray-900">Profile Information</h2>
            <div className="flex items-center space-x-4">
              <div
                data-testid="profile-avatar"
                className="size-16 shrink-0 overflow-hidden rounded-full"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name || 'User'}
                    className="size-full object-cover"
                  />
                ) : (
                  <div
                    className={`flex size-full items-center justify-center ${getAvatarColor(user.name)}`}
                  >
                    <span className="text-xl font-semibold text-white">
                      {getInitials(user.name)}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <p data-testid="profile-name" className="text-lg font-semibold text-gray-900">
                  {user.name || '—'}
                </p>
                <p data-testid="profile-email" className="text-sm text-gray-600">
                  {user.email || '—'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Balance Card */}
        <div className="animate-fade-in-up h-full opacity-0" style={{ animationDelay: '200ms' }}>
          {dashboardLoading && (
            <div className="flex h-full min-h-[200px] items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
          {dashboardError && (
            <div className="flex h-full min-h-[200px] items-center">
              <ErrorDisplay error={dashboardError} onRetry={refetch} />
            </div>
          )}
          {!dashboardLoading && !dashboardError && dashboardData && (
            <BalanceCard
              balance={dashboardData.balance}
              nextTopUpDate={dashboardData.nextTopUpDate}
            />
          )}
        </div>
      </div>

      <div
        className="animate-fade-in-up mt-4 flex justify-end opacity-0"
        style={{ animationDelay: '300ms' }}
      >
        <Link
          to="/claims"
          className="flex items-center gap-1 text-sm font-semibold text-red-500 hover:text-red-600"
        >
          View All History
          <ChevronRight className="size-4" />
        </Link>
      </div>
    </PageContainer>
  );
}

export { Profile };
